import React, { memo, useEffect, useState } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    useTheme,
} from '@mui/material';
import { DateString } from 'routes/HumanResourcesModule/business/types';
import { MANTY_STATUS } from 'routes/HumanResourcesModule/business/definitions';
import {
    getPositionValue,
    PositionMappingValue,
} from 'routes/HumanResourcesModule/Settings/HrPositionMapping/helpers/gridHelpers';
import { orgChartHrFormatter } from 'routes/HumanResourcesModule/components/HrDataGrid/helpers/orgChartHrColumnHelpers';

import { HrSimulation } from 'routes/HumanResourcesModule/Simulation/helpers/types';
import { getScopedT } from 'helpers/translationHelpers';
import {
    EnvelopeField,
    EnvelopeHypothesis,
    EnvelopeHypothesisMutationParams,
} from './helpers/types';
import EnvelopeHypothesisModalContent from './EnvelopeHypothesisModalContent';
import { useTranslation } from 'react-i18next';

const TRANSLATION_KEY = 'humanResources.simulation.envelope.creationModal';

type Props = {
    open: boolean;
    onClose: () => void;
    hrSimulation: HrSimulation;
    envelopeField: EnvelopeField;
    envelopeFieldValue: string;
    orgChartHrMapping: string;
    isNonRecurrentEnvelope?: boolean;
    orgChartHrSoftwareId: string;
    orgChartHrName: string;
    hypothesisCRUDOperations: Partial<{
        createHypothesis: (payload: EnvelopeHypothesisMutationParams) => void;
        editHypothesis: (payload: EnvelopeHypothesisMutationParams) => void;
        deleteHypothesis: () => void;
    }>;
    hypothesis?: EnvelopeHypothesis | null;
    isUpdateEnabled?: boolean;
};

const EnvelopeHypothesisModal = ({
    open,
    onClose,
    hrSimulation,
    envelopeField,
    isNonRecurrentEnvelope = false,
    envelopeFieldValue,
    orgChartHrMapping,
    orgChartHrSoftwareId,
    orgChartHrName,
    hypothesisCRUDOperations,
    hypothesis = null,
    isUpdateEnabled = true,
}: Props): React.ReactElement => {
    const theme = useTheme();
    const { t } = useTranslation();
    const tScoped = getScopedT(TRANSLATION_KEY, t);

    const initialComment = hypothesis?.comment ?? '';
    const [comment, setComment] = useState(initialComment);

    const [hasChanges, setHasChanges] = useState(false);

    const [valuesByDates, setValuesByDates] = useState({});

    useEffect(() => {
        setHasChanges(comment !== initialComment);
    }, [comment, initialComment]);

    const onValuesByDatesChange = (
        newValuesByDates: Record<DateString, number>,
    ) => {
        setValuesByDates(newValuesByDates);
    };

    const { createHypothesis, editHypothesis, deleteHypothesis } =
        hypothesisCRUDOperations;

    const onValidate = () => {
        if (hypothesis !== null) {
            editHypothesis?.({
                comment,
                valuesByDates,
                oldValuesByDates: hypothesis.newValuesByDates,
            });
        } else {
            createHypothesis?.({
                comment,
                valuesByDates,
            });
        }
        onClose();
    };

    const onDelete = () => {
        if (hypothesis !== null) {
            deleteHypothesis?.();
        }
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="lg"
            PaperProps={{
                sx: { backgroundColor: theme.palette.lightGrey },
            }}
        >
            <DialogTitle variant="h4" color="primary">
                {tScoped(`${envelopeField}.title`, {
                    [envelopeField]: (envelopeField === MANTY_STATUS
                        ? getPositionValue(
                              envelopeFieldValue as PositionMappingValue,
                              true,
                          )
                        : envelopeFieldValue
                    ).toLowerCase(),
                    orgChartHr: orgChartHrFormatter({
                        orgChartHrName,
                        orgChartHrMapping,
                    }),
                })}
            </DialogTitle>
            <DialogContent>
                <EnvelopeHypothesisModalContent
                    hrSimulation={hrSimulation}
                    isNonRecurrentEnvelope={isNonRecurrentEnvelope}
                    orgChartHrSoftwareId={orgChartHrSoftwareId}
                    envelopeFieldValue={envelopeFieldValue}
                    envelopeField={envelopeField}
                    onValuesByDatesChange={onValuesByDatesChange}
                    setHasChanges={setHasChanges}
                    comment={comment}
                    setComment={setComment}
                    commentFieldLabel={tScoped('comment')}
                    hypothesis={hypothesis}
                    isUpdateEnabled={isUpdateEnabled}
                    translationKey={TRANSLATION_KEY}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>
                    {t(isUpdateEnabled ? 'common.cancel' : 'common.close')}
                </Button>
                {isUpdateEnabled && (
                    <>
                        {hypothesis !== null && (
                            <Button
                                variant="contained"
                                color="error"
                                onClick={onDelete}
                            >
                                {t('common.delete')}
                            </Button>
                        )}
                        <Button
                            disabled={!hasChanges}
                            variant="contained"
                            onClick={onValidate}
                        >
                            {t('common.validate')}
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default memo(EnvelopeHypothesisModal);
